import { useEffect, useState } from 'react';
import { http } from '@/api/http';

/**
 * Fetches an authenticated resource via `http` (bearer header attached by the interceptor),
 * returns an object URL, and revokes it on unmount. CONTRACTS.md §10 — imports `{ http }`.
 * Used by effect-pack thumbnails (byte[] endpoints) and any authenticated media.
 */
export function useAuthedBlob(url: string | undefined): {
  src: string | undefined;
  isLoading: boolean;
  error?: unknown;
} {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  useEffect(() => {
    if (!url) {
      setSrc(undefined);
      return;
    }
    let objectUrl: string | undefined;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    const run = async () => {
      try {
        const res = await http.get<Blob>(url, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return { src, isLoading, error };
}
